import { useCallback, useRef, useState, type RefObject } from 'react';

import { useIsomorphicEffect } from '@/hooks/useIsomorphicEffect/useIsomorphicEffect';

export interface UseResizeHeightOptions {
  onHeightChange?: (height: number) => void;
}

export interface UseResizeHeightResult<T extends HTMLElement> {
  ref: RefObject<T>;
  height: number;
  /**
   * height state 를 0 으로 reset.
   *
   * 외부에서 spacer 등의 inline style 을 직접 조작했을 때, 다음 ResizeObserver 측정값이
   * 이전 state 와 같으면 setHeight skip 으로 React 가 inline 을 재적용 못 하는 race 가 있다.
   * reset() 으로 state 를 비워두면 다음 measure 가 무조건 setHeight 를 호출해 inline 이 재적용된다.
   */
  reset: () => void;
}

export function useResizeHeight<T extends HTMLElement = HTMLElement>(
  options?: UseResizeHeightOptions,
): UseResizeHeightResult<T> {
  const { onHeightChange } = options ?? {};

  const ref = useRef<T>(null);
  const [height, setHeight] = useState(0);

  const onChangeRef = useRef(onHeightChange);
  onChangeRef.current = onHeightChange;

  const reset = useCallback(() => {
    setHeight(0);
  }, []);

  useIsomorphicEffect(() => {
    const el = ref.current;
    if (!el) return;

    let frameId: number | null = null;
    let hydrationRaf: number | null = null;

    const measure = (): void => {
      const next = el.offsetHeight;

      setHeight((prev) => {
        if (prev === next) return prev;

        onChangeRef.current?.(next);
        return next;
      });
    };

    // 초기 1회 (paint 전에 실행됨).
    // 이 시점은 자식 컴포넌트의 hydration 또는 비동기 mount 가 완료되지 않았을 수 있어
    // offsetHeight 가 stale 한 작은 값일 가능성이 있다.
    measure();

    // race 가드 — double RAF.
    // React 18 의 effects → paint → 다음 frame 사이에 자식 hydration / 비동기 mount 가
    // 완료될 시간을 확보한 뒤 한 번 더 측정한다. ResizeObserver 가 어떤 이유로
    // 첫 콜백을 skip 하는 케이스(브라우저 RO loop detection 등)에서 보조 안전망 역할.
    hydrationRaf = requestAnimationFrame(() => {
      hydrationRaf = requestAnimationFrame(measure);
    });

    if (typeof ResizeObserver === 'undefined') {
      return () => {
        if (hydrationRaf !== null) cancelAnimationFrame(hydrationRaf);
      };
    }

    const observer = new ResizeObserver(() => {
      if (frameId !== null) cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(measure);
    });

    observer.observe(el);

    return () => {
      observer.disconnect();
      if (frameId !== null) cancelAnimationFrame(frameId);
      if (hydrationRaf !== null) cancelAnimationFrame(hydrationRaf);
    };
  }, []);

  return { ref, height, reset };
}
